import { useEffect, useEffectEvent, useRef, useState } from 'react';

import type { VisibilityState } from '@tanstack/react-table';
import { useLocation } from 'react-router';

import { SHOW_SELECTED_URL_PARAM } from '@/config/constants';
import type { SymbolKey } from '@/entities/symbol';
import { useAppStore } from '@/store/hooks';
import { defaultVisibilityState, forwardCurvesColumns, spotColumns } from '../constants';
import { useFilter } from '../state/useFilter';

import { useCatalogColumnFilters } from './useCatalogColumnFilters';
import { useCatalogData } from './useCatalogData';
import { useCatalogSorting } from './useCatalogSorting';
import { useCatalogTableInstance } from './useCatalogTableInstance';
import { useFilteredCatalogData } from './useFilteredCatalogData';

const useCatalog = (isForwardCurves = false) => {
  const location = useLocation();
  const { filters, disableSearchParams } = useFilter();
  const store = useAppStore();

  // Freeze the visible symbol set the moment the toggle turns ON; ignore selection
  // changes while the toggle is active so clearing + re-selecting doesn't shift the view.
  const frozenSymbolsRef = useRef<SymbolKey[]>([]);
  const prevShowSelectedRef = useRef(false);
  const showSelectedNow = filters[SHOW_SELECTED_URL_PARAM];
  if (showSelectedNow && !prevShowSelectedRef.current) {
    frozenSymbolsRef.current = store.getState().symbolSelector.symbols;
  }
  prevShowSelectedRef.current = showSelectedNow;

  const columns = isForwardCurves ? forwardCurvesColumns : spotColumns;

  const [activeTab, setActiveTab] = useState<number>(isForwardCurves ? 1 : 0);
  const [showDiscontinued, setShowDiscontinued] = useState<boolean>(false);
  const [expandedData, setExpandedData] = useState<boolean>(false);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(defaultVisibilityState);

  const { catalog, status } = useCatalogData(isForwardCurves);
  const { sorting, onSortingChange } = useCatalogSorting(isForwardCurves, disableSearchParams);
  const columnFilters = useCatalogColumnFilters({ filters, showDiscontinued, frozenSymbolsRef });

  const clearState = useEffectEvent(() => {
    setFilterOpen(false);
    setColumnVisibility(prevState => ({
      ...prevState,
      TimeRef: isForwardCurves || expandedData,
      PeriodType: isForwardCurves || expandedData
    }));
  });

  useEffect(() => {
    // oxlint-disable-next-line react-hooks-js/set-state-in-effect
    clearState();
    setActiveTab(isForwardCurves ? 1 : 0);
  }, [location.pathname, isForwardCurves]);

  const filteredData = useFilteredCatalogData(catalog, expandedData, isForwardCurves);
  const globalFilter = filters.search ?? '';

  const table = useCatalogTableInstance({
    data: filteredData,
    columns,
    columnVisibility,
    sorting,
    columnFilters,
    globalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange
  });

  return {
    status,
    filterOpen,
    setFilterOpen,
    filteredData,
    table,
    activeTab,
    setActiveTab,
    isForwardCurves,
    expandedData,
    setExpandedData,
    showDiscontinued,
    setShowDiscontinued
  };
};

export default useCatalog;
